import { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { UserDataContext } from "../../context/User";
import useAccess from "../../hooks/useAccess";
import UpgradePrompt from "./UpgradePrompt";

/**
 * ProtectedRoute
 *
 * Props:
 *   requireAuth   (bool)    — must be logged in. Default true.
 *   minTier       (string)  — minimum tier key required (e.g. "wanderer")
 *   allowedTiers  (array)   — exact list of allowed tier keys
 *   requireActive (bool)    — subscription must be active. Default true.
 *   requireRole   (string)  — role key required (e.g. "reviewer", "mentor")
 *   redirectTo    (string)  — where to send unauthenticated users. Default "/login"
 */
const ProtectedRoute = ({
  children,
  requireAuth = true,
  minTier = null,
  allowedTiers = null,
  requireActive = true,
  requireRole = null,
  redirectTo = "/login",
}) => {
  const { isInitialized } = useContext(UserDataContext);
  const { isLoggedIn, isAdmin, isReviewer, isMentor, tierKey, meetsMinTier, hasAccess, isActive } = useAccess();
  const location = useLocation();

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (requireAuth && !isLoggedIn) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }

  // Role check — before admin bypass so reviewers/mentors who are also admins still pass
  if (requireRole) {
    const roleMap = { reviewer: isReviewer, mentor: isMentor };
    const allowed = isAdmin || roleMap[requireRole];
    if (!allowed) {
      return <Navigate to="/" replace />;
    }
    return children;
  }

  // Admins bypass all tier checks
  if (isAdmin) return children;

  if (requireAuth && requireActive && isLoggedIn && tierKey !== "free" && !isActive) {
    return <UpgradePrompt reason="inactive" currentTier={tierKey} />;
  }

  if (allowedTiers && allowedTiers.length > 0 && !hasAccess(allowedTiers)) {
    return (
      <UpgradePrompt
        reason="tier"
        currentTier={tierKey}
        requiredTier={allowedTiers[0]}
        allowedTiers={allowedTiers}
      />
    );
  }

  if (minTier && !meetsMinTier(minTier)) {
    return <UpgradePrompt reason="tier" currentTier={tierKey} requiredTier={minTier} />;
  }

  return children;
};

export default ProtectedRoute;
